import { Auth, Hub } from "aws-amplify";
import { AuthState, ChallengeName } from "@aws-amplify/ui-components";
import { parse } from "query-string";
import sigV4Client from "./sigv4Client";
import { AUTH_INVALID_CODE } from "./constants";
import InvalidMfaCodeError from "../models/errors/invalid-mfa-code-error";

export const AUTH_STATE_CHANGE_EVENT = "AuthStateChange";
export const AUTH_CHANNEL = "UI Auth";

// Determine environment: true if title includes 'Dev'
const isDevEnv =
  typeof process !== "undefined" &&
  process.env.REACT_APP_TITLE &&
  process.env.REACT_APP_TITLE.includes("Dev");

// Utility for logging only in dev
function debugLog(...args) {
  if (isDevEnv) {
    console.log(...args);
  }
}

/*
  Sign up a user
*/
export const cognitoSignUp = async (username, password, attributes) => {
  const { user } = await Auth.signUp({ username, password, attributes });
  debugLog("cognitoSignUp → user:", user);
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: AuthState.ConfirmSignUp,
    user,
  });
  return user;
};

/*
  Sign in: first use default SRP to detect NEW_PASSWORD_REQUIRED.
  If NEW_PASSWORD_REQUIRED, handle reset password.
  Else if SRP returns session (existing user), then:
    - in non-dev: enforce OTP by signing out and re-signing in under CUSTOM_AUTH
    - in dev: skip OTP and proceed with SRP session
*/
export const cognitoSignIn = async (username, password) => {
  try {
   //  Ensure default SRP flow for initial check
    Auth.configure({ authenticationFlowType: "USER_SRP_AUTH" });
    const data = await Auth.signIn(username, password);
    debugLog("Initial SRP cognitoSignIn →", JSON.stringify(data, null, 2));

    // Handle NEW_PASSWORD_REQUIRED
    if (data.challengeName === ChallengeName.NewPasswordRequired) {
      debugLog("Challenge NewPasswordRequired");
      await Hub.dispatch(AUTH_CHANNEL, {
        event: AUTH_STATE_CHANGE_EVENT,
        message: AuthState.ResetPassword,
        data,
      });
      return data;
    }

    // Existing user: SRP returned a session
    if (!isDevEnv) {
      debugLog("Existing user: enforcing OTP. Signing out SRP session.");
      // Sign out SRP session
      try {
        await Auth.signOut();
      } catch (e) {
        debugLog("SignOut after SRP session:", e);
      }
      // Switch to CUSTOM_AUTH for OTP
      Auth.configure({ authenticationFlowType: "CUSTOM_AUTH" });
      // Re-sign in under CUSTOM_AUTH
      const customData = await Auth.signIn(username, password);
      debugLog("cognitoSignIn under CUSTOM_AUTH →", JSON.stringify(customData, null, 2));

      if (customData.challengeName === ChallengeName.CustomChallenge) {
        debugLog("Challenge CustomChallenge (OTP) for existing user");
        await Hub.dispatch(AUTH_CHANNEL, {
          event: AUTH_STATE_CHANGE_EVENT,
          message: AuthState.ConfirmSignIn,
          data: customData,
        });
      } else if (customData.challengeName === ChallengeName.MFASetup) {
        await Hub.dispatch(AUTH_CHANNEL, {
          event: AUTH_STATE_CHANGE_EVENT,
          message: AuthState.TOTPSetup,
          data: customData,
        });
      } else if (customData.challengeName === ChallengeName.NewPasswordRequired) {
        
        await Hub.dispatch(AUTH_CHANNEL, {
          event: AUTH_STATE_CHANGE_EVENT,
          message: AuthState.ResetPassword,
          data: customData,
        });
      } else {
        // No challenge: proceed as signed in
        await Hub.dispatch(AUTH_CHANNEL, {
          event: AUTH_STATE_CHANGE_EVENT,
          message: AuthState.SignedIn,
          data: customData,
        });
      }
      return customData;
    } else {
      // Dev: skip OTP
      debugLog("Dev environment: skipping OTP, SignedIn via SRP session");
      await Hub.dispatch(AUTH_CHANNEL, {
        event: AUTH_STATE_CHANGE_EVENT,
        message: AuthState.SignedIn,
        data,
      });
      return data;
    }
  } catch (err) {
    debugLog("cognitoSignIn error:", err);
    throw err;
  }
};

/*
  Complete NEW_PASSWORD_REQUIRED, then in non-dev switch to CUSTOM_AUTH and re-sign in to trigger OTP.
*/
export const cognitoCompleteNewPassword = async (user, newPassword, requiredAttributes = {}) => {
  try {
    debugLog("Calling completeNewPassword …");
    // Complete under default SRP flow
    const result = await Auth.completeNewPassword(user, newPassword, requiredAttributes);
    debugLog("completeNewPassword →", JSON.stringify(result, null, 2));

    if (!isDevEnv) {
      // After successful password change, switch to CUSTOM_AUTH for OTP
      Auth.configure({ authenticationFlowType: "CUSTOM_AUTH" });
      debugLog("Configured CUSTOM_AUTH for OTP in non-dev");
    } else {
      debugLog("Dev environment: skipping CUSTOM_AUTH configuration");
    }

    const username =
      typeof user.getUsername === "function" ? user.getUsername() : user.username || user;
    debugLog("Re-signing in to continue flow…");
    const next = await Auth.signIn(username, newPassword);
    debugLog("After re-signIn →", JSON.stringify(next, null, 2));

    if (next.challengeName === ChallengeName.CustomChallenge) {
      await Hub.dispatch(AUTH_CHANNEL, {
        event: AUTH_STATE_CHANGE_EVENT,
        message: AuthState.ConfirmSignIn,
        data: next,
      });
    } else if (next.challengeName === ChallengeName.MFASetup) {
      await Hub.dispatch(AUTH_CHANNEL, {
        event: AUTH_STATE_CHANGE_EVENT,
        message: AuthState.TOTPSetup,
        data: next,
      });
    } else if (next.challengeName === ChallengeName.NewPasswordRequired) {
      await Hub.dispatch(AUTH_CHANNEL, {
        event: AUTH_STATE_CHANGE_EVENT,
        message: AuthState.ResetPassword,
        data: next,
      });
    } else {
      await Hub.dispatch(AUTH_CHANNEL, {
        event: AUTH_STATE_CHANGE_EVENT,
        message: AuthState.SignedIn,
        data: next,
      });
    }

    return next;
  } catch (err) {
    debugLog("cognitoCompleteNewPassword error:", err);
    throw err;
  }
};

/*
  Confirm sign-in for CUSTOM_CHALLENGE (OTP).
*/
export const cognitoConfirmSignIn = async (user, otp) => {
  try {
    debugLog("Sending OTP answer:", otp);
    const data = await Auth.sendCustomChallengeAnswer(user, otp);
    debugLog("sendCustomChallengeAnswer →", JSON.stringify(data, null, 2));

    if (data.challengeName === ChallengeName.CustomChallenge) {
      throw new InvalidMfaCodeError(AUTH_INVALID_CODE);
    }

    let nextState = AuthState.SignedIn;
    if (data.challengeName === ChallengeName.MFASetup) {
      nextState = AuthState.TOTPSetup;
    } else if (data.challengeName === ChallengeName.NewPasswordRequired) {
      nextState = AuthState.ResetPassword;
    }

    await Hub.dispatch(AUTH_CHANNEL, {
      event: AUTH_STATE_CHANGE_EVENT,
      message: nextState,
      data,
    });
    return data;
  } catch (err) {
    debugLog("cognitoConfirmSignIn error:", err);
    throw err;
  }
};

/*
  Sign out
*/
export const cognitoSignOut = async () => {
  try {
    await Auth.signOut();
  } catch (err) {
    debugLog("cognitoSignOut warning:", err);
  }
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: AuthState.SignedOut,
  });
};

/*
  Forgot password flows
*/
export const cognitoSendCode = async (username) => {
  debugLog("cognitoSendCode for:", username);
  return Auth.forgotPassword(username);
};
export const cognitoForgotPassword = async (username, code, password) => {
  debugLog("cognitoForgotPassword for:", username);
  return Auth.forgotPasswordSubmit(username, code, password);
};

/*
  Manual auth state change dispatch
*/
export const changeAuthState = async (nextState, data) => {
  debugLog("changeAuthState →", nextState, data);
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: nextState,
    data,
  });
};

/*
  Public session helpers
*/
export const authenticatePublicSession = (sessionData) => {
  debugLog("authenticatePublicSession:", sessionData);
  const creds = parse(atob(sessionData));
  const tokenPrefix = "DigitalPortalPublicUser";
  const referenceNum = atob(creds.sessionId).split(":")[1];

  localStorage.setItem(`${tokenPrefix}SessionId`, creds.sessionId.replace(/ /g, "+"));
  localStorage.setItem(`${tokenPrefix}AccessId`, creds.accessId.replace(/ /g, "+"));
  localStorage.setItem(`${tokenPrefix}SecretKey`, creds.secretKey.replace(/ /g, "+"));
  localStorage.setItem(`${tokenPrefix}SessionToken`, creds.sessionToken.replace(/ /g, "+"));
  localStorage.setItem(`${tokenPrefix}TransactionId`, referenceNum);
  localStorage.setItem("PUBLIC_SESSION", "true");
};

export const currentPublicSessionCredentials = () => {
  const p = "DigitalPortalPublicUser";
  return {
    accessKeyId: localStorage.getItem(`${p}AccessId`),
    secretAccessKey: localStorage.getItem(`${p}SecretKey`),
    sessionToken: localStorage.getItem(`${p}SessionToken`),
  };
};

export const getSessionId = () =>
  localStorage.getItem("DigitalPortalPublicUserSessionId");
export const getTransactionId = () =>
  localStorage.getItem("DigitalPortalPublicUserTransactionId");

/*
  SigV4 signer
*/
export const sign = (credentials, method, endpoint, path, queryParams = null, body = null, headers = {}) => {
  debugLog("Signing request:", method, endpoint + path);
  return sigV4Client
    .newClient({
      accessKey: credentials.accessKeyId,
      secretKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: "ap-southeast-1",
      endpoint,
    })
    .signRequest({ method, path, headers, queryParams, body }).headers;
};

/*
  Token/role helpers
*/
export const getAuthenticatedRole = (user) => {
  try {
    const groups = user.signInUserSession.idToken.payload["cognito:groups"];
    return groups && groups.length ? groups[0] : "";
  } catch {
    return "";
  }
};

export const getAuthenticatedIdToken = async () => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken();
  // debugLog("getAuthenticatedIdToken →", token);
  return token;
};
